import React, { useState, useCallback } from 'react';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Key, User, BookOpen } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

// InputField component
const InputField = ({ 
  type, 
  name, 
  placeholder, 
  icon: Icon, 
  showToggle = false,
  showPassword,
  onToggleShow,
  value,
  onChange,
  validationError,
  disabled,
  strengthIndicator
}) => (
  <div className="relative group">
    <Icon className="absolute left-4 top-4 w-5 h-5 text-gray-400 transition-colors group-focus-within:text-[#00153F] z-10" />
    <input
      type={showToggle && showPassword ? 'text' : type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={`w-full pl-12 ${showToggle ? 'pr-12' : 'pr-4'} py-4 border-2 rounded-xl transition-all duration-300 ${
        validationError 
          ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' 
          : 'border-gray-200 focus:ring-2 focus:ring-[#00153F]/20 focus:border-[#00153F] hover:border-gray-300 bg-white/90 backdrop-blur-sm'
      } disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 placeholder-gray-500`}
      disabled={disabled}
      autoComplete={type === 'password' ? 'new-password' : 'off'}
    />
    {showToggle && (
      <button
        type="button"
        onClick={onToggleShow}
        className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        disabled={disabled}
        tabIndex={-1}
      >
        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    )}
    {strengthIndicator}
    {validationError && (
      <div className="mt-2 text-sm text-red-600 flex items-center animate-slideIn">
        <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
        <span>{validationError}</span>
      </div>
    )}
  </div>
);

const FirstTimePasswordSetup = () => {
  const { user, setPassword, loading, error } = useAuth();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validatePassword = useCallback((password) => {
    const errors = {};
    
    if (!password) {
      errors.password = 'La contraseña es requerida';
    } else if (password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
    } else if (password.length > 50) {
      errors.password = 'La contraseña no puede tener más de 50 caracteres';
    }
    
    return errors;
  }, []);

  const validateConfirmPassword = useCallback((password, confirmPassword) => {
    const errors = {};
    
    if (!confirmPassword) {
      errors.confirmPassword = 'Confirma tu contraseña';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden';
    }
    
    return errors;
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    console.log(`🔄 Input changed: ${name} = ${value.length} chars`);
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      if (name === 'password') {
        const passwordErrors = validatePassword(value);
        const confirmErrors = validateConfirmPassword(value, prev.confirmPassword);
        setValidationErrors(prevErrors => ({
          ...prevErrors,
          ...passwordErrors,
          ...confirmErrors
        }));
      } else if (name === 'confirmPassword') {
        const confirmErrors = validateConfirmPassword(prev.password, value);
        setValidationErrors(prevErrors => ({
          ...prevErrors,
          ...confirmErrors
        }));
      }
      
      return newData;
    });

    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  }, [validatePassword, validateConfirmPassword, validationErrors]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    console.log('🔄 Form submitted');
    
    if (isSubmitting) {
      console.log('⚠️ Submit already in progress, ignoring...');
      return;
    }

    console.log('🔑 Starting password setup process...');
    
    const passwordErrors = validatePassword(formData.password);
    const confirmErrors = validateConfirmPassword(formData.password, formData.confirmPassword);
    const allErrors = { ...passwordErrors, ...confirmErrors };
    
    if (Object.keys(allErrors).length > 0) {
      console.log('❌ Validation errors found:', allErrors);
      setValidationErrors(allErrors);
      return;
    }

    console.log('✅ Validation passed, submitting...');

    try {
      setIsSubmitting(true);
      setValidationErrors({});
      
      console.log('📤 Calling setPassword function...');
      console.log(`Password: "${formData.password}" (${formData.password.length} chars)`);
      console.log('User:', user);
      console.log('Auth loading:', loading);
      console.log('Auth error:', error);
      
      const token = localStorage.getItem('authToken');
      console.log('Token available:', token ? 'Yes' : 'No');
      if (token) {
        console.log('Token preview:', token.substring(0, 20) + '...');
      }
      
      const result = await setPassword(formData.password);
      
      console.log('✅ Password set successfully:', result);
      
    } catch (err) {
      console.error('❌ Password setup failed:', err);
      
      const errorMessage = err.message || 'Error al establecer la contraseña. Inténtalo de nuevo.';
      setValidationErrors({ 
        submit: errorMessage 
      });
      
    } finally {
      setIsSubmitting(false);
      console.log('🏁 Submit process finished');
    }
  }, [isSubmitting, formData.password, formData.confirmPassword, validatePassword, validateConfirmPassword, setPassword, user, loading, error]);

  const getPasswordStrength = useCallback((password) => {
    if (!password) return { strength: 0, label: '', color: 'bg-gray-200' };
    
    let strength = 0;
    const checks = [
      password.length >= 6,
      password.length >= 10,
      /[A-Z]/.test(password) || /[a-z]/.test(password),
      /\d/.test(password),
      /[!@#$%^&*(),.?":{}|<>]/.test(password)
    ];
    
    strength = checks.filter(Boolean).length;
    
    if (strength <= 2) {
      return { strength: 1, label: 'Débil', color: 'bg-red-500' };
    } else if (strength <= 3) {
      return { strength: 2, label: 'Regular', color: 'bg-yellow-500' };
    } else {
      return { strength: 3, label: 'Fuerte', color: 'bg-green-500' };
    }
  }, []);

  const passwordStrength = getPasswordStrength(formData.password);

  console.log('🔍 FirstTimePasswordSetup render:', {
    isSubmitting,
    hasPassword: !!formData.password,
    hasConfirmPassword: !!formData.confirmPassword,
    hasValidationErrors: Object.keys(validationErrors).length > 0,
    authLoading: loading,
    authError: error,
    user: user?.username
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#00153F] via-[#001a4d] to-gray-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#00153F]/20 to-transparent"></div>
        <div className="absolute top-1/4 left-1/3 w-2 h-2 bg-[#3366cc] rounded-full animate-pulse opacity-60"></div>
        <div className="absolute top-2/3 left-1/4 w-1 h-1 bg-[#4477dd] rounded-full animate-pulse opacity-40" style={{animationDelay: '100ms'}}></div>
        <div className="absolute top-1/2 right-1/3 w-1.5 h-1.5 bg-[#2255bb] rounded-full animate-pulse opacity-50" style={{animationDelay: '200ms'}}></div>
        <div className="absolute bottom-1/4 right-1/4 w-2 h-2 bg-[#3366cc] rounded-full animate-pulse opacity-30" style={{animationDelay: '300ms'}}></div>
        <div className="absolute top-3/4 left-2/3 w-1 h-1 bg-[#4477dd] rounded-full animate-pulse opacity-70" style={{animationDelay: '500ms'}}></div>
        <div className="absolute top-1/6 right-1/6 w-1.5 h-1.5 bg-[#3366cc] rounded-full animate-pulse opacity-40" style={{animationDelay: '700ms'}}></div>
      </div>

      <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md relative z-10 border border-white/20 animate-fadeIn">
        <div className="text-center mb-6 sm:mb-8">
          <div className="mb-4 sm:mb-6">
            <div className="relative mx-auto w-20 h-20 sm:w-24 sm:h-24 mb-3 sm:mb-4">
              <div className="bg-[#00153F] w-full h-full rounded-full flex items-center justify-center shadow-lg ring-4 ring-[#00153F]/20">
                <BookOpen className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
              </div>
              <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 w-7 h-7 sm:w-8 sm:h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg animate-bounce">
                <Key className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
          
          <h1 className="text-2xl sm:text-3xl font-bold text-[#00153F] mb-2 sm:mb-3">¡Bienvenido!</h1>
          <p className="text-sm sm:text-base text-gray-600 mb-1 sm:mb-2">
            <span className="font-semibold text-[#00153F]">{user?.first_name} {user?.last_name}</span>
          </p>
          <p className="text-xs sm:text-sm text-gray-500 mb-2">Por favor establece tu contraseña para continuar</p>
          <p className="text-xs sm:text-sm text-[#00153F] font-medium">Universidad de Asesores</p>
          <p className="text-xs text-gray-500">Puerto Madero Urubó</p>
        </div>

        {/* User Info Display */}
        <div className="bg-gradient-to-r from-[#00153F]/5 to-indigo-50 rounded-xl p-3 sm:p-4 border-2 border-[#00153F]/20 mb-4 sm:mb-6">
          <div className="flex items-center space-x-3">
            <div className="bg-white rounded-full p-2 shadow-sm">
              <User className="w-4 h-4 sm:w-5 sm:h-5 text-[#00153F]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                {user?.username}
              </p>
              <p className="text-xs text-gray-600 truncate">
                {user?.email}
              </p>
            </div>
          </div>
        </div>

        {/* Global Error Message */}
        {(error || validationErrors.submit) && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 animate-slideIn">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2 sm:mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-red-800 mb-1">Error al configurar contraseña</h4>
                <p className="text-xs sm:text-sm text-red-700">
                  {validationErrors.submit || error}
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Password Field */}
          <InputField
            type="password"
            name="password"
            placeholder="Nueva contraseña (mín. 6 caracteres)"
            icon={Lock}
            showToggle={true}
            showPassword={showPassword}
            onToggleShow={() => setShowPassword(!showPassword)}
            value={formData.password}
            onChange={handleInputChange}
            validationError={validationErrors.password}
            disabled={isSubmitting}
            strengthIndicator={
              formData.password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-600">Fortaleza:</span>
                    <span className={`font-medium ${
                      passwordStrength.strength === 1 ? 'text-red-600' :
                      passwordStrength.strength === 2 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div 
                      className={`h-2 rounded-full transition-all duration-500 ${passwordStrength.color}`}
                      style={{ width: `${(passwordStrength.strength / 3) * 100}%` }}
                    />
                  </div>
                </div>
              )
            }
          />

          {/* Confirm Password Field */}
          <InputField
            type="password"
            name="confirmPassword"
            placeholder="Confirmar contraseña"
            icon={Lock}
            showToggle={true}
            showPassword={showConfirmPassword}
            onToggleShow={() => setShowConfirmPassword(!showConfirmPassword)}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            validationError={validationErrors.confirmPassword}
            disabled={isSubmitting}
            strengthIndicator={
              formData.confirmPassword && (
                <div className="mt-2 flex items-center">
                  {formData.password === formData.confirmPassword ? (
                    <div className="flex items-center text-green-600 text-xs sm:text-sm animate-slideIn">
                      <CheckCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span>Las contraseñas coinciden</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-red-600 text-xs sm:text-sm animate-slideIn">
                      <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span>Las contraseñas no coinciden</span>
                    </div>
                  )}
                </div>
              )
            }
          />

          {/* Password Requirements */}
          <div className="bg-[#00153F]/5 border-2 border-[#00153F]/20 rounded-xl p-3 sm:p-4">
            <h4 className="text-xs sm:text-sm font-semibold text-[#00153F] mb-2 sm:mb-3">Requisitos de la contraseña:</h4>
            <ul className="space-y-1.5 sm:space-y-2">
              <li className="flex items-center text-xs sm:text-sm">
                <CheckCircle className={`w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 flex-shrink-0 transition-colors duration-300 ${
                  formData.password.length >= 6 ? 'text-green-500' : 'text-gray-300'
                }`} />
                <span className={formData.password.length >= 6 ? 'text-green-700 font-medium' : 'text-gray-700'}>
                  Al menos 6 caracteres
                </span>
              </li>
              <li className="flex items-center text-xs sm:text-sm">
                <CheckCircle className={`w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 flex-shrink-0 transition-colors duration-300 ${
                  /\d/.test(formData.password) ? 'text-green-500' : 'text-gray-300'
                }`} />
                <span className={/\d/.test(formData.password) ? 'text-green-700 font-medium' : 'text-gray-700'}>
                  Al menos un número (recomendado)
                </span>
              </li>
              <li className="flex items-center text-xs sm:text-sm">
                <CheckCircle className={`w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 flex-shrink-0 transition-colors duration-300 ${
                  /[A-Z]/.test(formData.password) ? 'text-green-500' : 'text-gray-300'
                }`} />
                <span className={/[A-Z]/.test(formData.password) ? 'text-green-700 font-medium' : 'text-gray-700'}>
                  Al menos una mayúscula (recomendado)
                </span>
              </li>
            </ul>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || loading || 
              !formData.password || 
              !formData.confirmPassword || 
              formData.password !== formData.confirmPassword ||
              formData.password.length < 6
            }
            className={`w-full py-3.5 sm:py-4 rounded-xl font-medium text-white transition-all duration-300 flex items-center justify-center text-base sm:text-lg ${
              isSubmitting || loading
                ? 'bg-gray-400 cursor-not-allowed'
                : (formData.password && 
                   formData.confirmPassword && 
                   formData.password === formData.confirmPassword && 
                   formData.password.length >= 6)
                  ? 'bg-[#00153F] hover:bg-[#001a4d] transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl'
                  : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting || loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Configurando...
              </>
            ) : (
              'Establecer Contraseña'
            )}
          </button>
        </form>

        {/* Security Notice */}
        <div className="mt-6 sm:mt-8">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-3 sm:p-4 border border-green-200">
            <p className="text-xs sm:text-sm text-center text-green-800">
              🔒 Tu contraseña se almacena de forma <strong>segura y encriptada</strong>
            </p>
          </div>
          
          <div className="border-t border-gray-200 mt-4 pt-4">
            <p className="text-xs text-center text-gray-400">
              © 2024 Puerto Madero Urubó. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out; }
        .animate-slideIn { animation: slideIn 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default FirstTimePasswordSetup;